import { useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { ArrowDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { uploadsAtom } from "../../state/files.state";
import { labelingParamsAtom } from "../../state";
import { FileTree } from "../../Labeling.types";
import { TreeRootItem } from "./TreeRootItem";
import { useApiQuery } from "@/api/hooks/useApiQuery";
import { getDrafts } from "@/entities/DataAssociation/DataAssociation.queries";
import { DraftStatus } from "@/entities/DataAssociation/Draft.model";
import { formatFileName } from "../../Labeling.helpers";
import { formatDate } from "@/utils/misc/format";
import { StatementType } from "@/entities/DataAssociation/File.model";
import { UploadsResponse, UploadWithChildren } from "../../labeling.queries";

function parseDateFromFilename(filename: string): string {
    const match = filename.match(/(?:cl|labeled)-(\d{14})/);
    if (!match) return "";
    const dateStr = match[1];
    const year = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day = dateStr.slice(6, 8);
    const hour = dateStr.slice(8, 10);
    const min = dateStr.slice(10, 12);
    const sec = dateStr.slice(12, 14);
    return `${year}-${month}-${day} ${hour}:${min}:${sec}.00000`;
}

function findUploadWithFile(
    data: UploadsResponse,
    fileId: string,
): string | null {
    const allUploads = [...data.unlabeled, ...data.labeled];
    for (const upload of allUploads) {
        if (upload.id === fileId) return upload.id;
        for (const cleaned of upload.cleanedChildren) {
            if (cleaned.id === fileId) return upload.id;
            if (cleaned.labeledFiles.some((lf) => lf.id === fileId)) {
                return upload.id;
            }
        }
    }
    return null;
}

export function UploadTreeList() {
    const { data, isLoading } = useAtomValue(uploadsAtom);
    const { selectedFileId } = useAtomValue(labelingParamsAtom);
    const [openId, setOpenId] = useState<string | null>(null);
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (data && !hasInitialized.current) {
            hasInitialized.current = true;
            const found = selectedFileId
                ? findUploadWithFile(data, selectedFileId)
                : null;
            setOpenId(found);
        }
    }, [data, selectedFileId]);

    if (isLoading && !data) return <div>Loading...</div>;

    if (!data) return null;

    return (
        <div className="flex flex-col gap-8">
            {data.unlabeled.length > 0 && (
                <div>
                    <h2 className="heading-s mb-3 text-slate-500">Unlabeled</h2>
                    <div className="flex flex-col gap-6">
                        {data.unlabeled.map((upload) => (
                            <UploadTreeItem
                                key={upload.id}
                                upload={upload}
                                isOpen={openId === upload.id}
                                onToggle={() =>
                                    setOpenId(
                                        openId === upload.id ? null : upload.id,
                                    )
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
            {data.labeled.length > 0 && (
                <div>
                    <h2 className="heading-s mb-3 text-slate-500">Labeled</h2>
                    <div className="flex flex-col gap-6">
                        {data.labeled.map((upload) => (
                            <UploadTreeItem
                                key={upload.id}
                                upload={upload}
                                isOpen={openId === upload.id}
                                onToggle={() =>
                                    setOpenId(
                                        openId === upload.id ? null : upload.id,
                                    )
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

type UploadTreeItemProps = {
    upload: UploadWithChildren;
    isOpen: boolean;
    onToggle: () => void;
};

export function UploadTreeItem({
    upload,
    isOpen,
    onToggle,
}: UploadTreeItemProps) {
    const draftList = useApiQuery(getDrafts);

    const fileTrees = useMemo((): FileTree[] => {
        return upload.cleanedChildren.map((cleaned) => {
            const fileDraft = draftList.data?.find(
                (d) => d.fileId === cleaned.id,
            );
            return {
                name: "Cleaned file",
                date: parseDateFromFilename(cleaned.name),
                id: cleaned.id,
                documentKind: StatementType.MIXED,
                draft: fileDraft
                    ? {
                          id: fileDraft.id,
                          status:
                              fileDraft.status === DraftStatus.COMMITTED
                                  ? "committed"
                                  : "uncommitted",
                          date: formatDate(fileDraft.createdAt, {
                              outputFormat: "MM/DD/YY",
                              dateFormat: undefined,
                          }),
                      }
                    : null,
                labeledFiles: cleaned.labeledFiles.map((lf) => ({
                    id: lf.id,
                    date: parseDateFromFilename(lf.name),
                })),
            };
        });
    }, [upload, draftList.data]);

    return (
        <div>
            <div className="flex items-center justify-between">
                <p className="body-small font-medium text-white">
                    {formatFileName(upload.name).replace(".arvo", "")}
                </p>
            </div>
            <span className={clsx("py-1", "flex items-center gap-3")}>
                <button
                    onClick={onToggle}
                    className={clsx(
                        "no-styles pl-0.5",
                        "text-button subdued flex items-center gap-1",
                        "hover:text-white",
                    )}
                >
                    {isOpen ? "Hide details" : "See details"}
                    <motion.span
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{
                            type: "spring",
                            bounce: 0,
                            duration: 0.4,
                        }}
                        className="flex"
                    >
                        <ArrowDownIcon className="size-3 text-gray-500" />
                    </motion.span>
                </button>
            </span>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: "auto" }}
                        exit={{ height: 0 }}
                        transition={{
                            type: "spring",
                            bounce: 0,
                            duration: 0.4,
                        }}
                        className="overflow-hidden"
                    >
                        {fileTrees.length > 0 ? (
                            <ul
                                className={clsx(
                                    "flex flex-col gap-7",
                                    "pl-0.5 pt-5",
                                )}
                            >
                                {fileTrees.map((tree) => (
                                    <TreeRootItem key={tree.id} {...tree} />
                                ))}
                            </ul>
                        ) : (
                            <p className="pt-3 text-sm text-slate-500">
                                No cleaned files yet
                            </p>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
